/**
 * Pure helpers for §7's "Скриншоты → FM" — pasting/attaching a screenshot into the AI chat.
 *
 * Kept separate from `screenshot-upload.service.ts` for the same reason `element-picker.util.ts` is
 * split from `element-picker.service.ts`: everything that touches HTTP/store is thin and untested,
 * everything worth pinning down with a test is pure and lives here.
 */

/** Mirrors `EXTENSION_BY_TYPE` in `apps/mcp-backend/src/images/image-import.ts` — kept in sync by hand, not imported, since the frontend cannot depend on a backend-only package. */
const SUPPORTED_IMAGE_TYPES: ReadonlySet<string> = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
]);

/** A fast client-side check only — the file manager upload is the real, authoritative gate. */
export function isSupportedImageFile(file: Pick<File, 'type'>): boolean {
    return SUPPORTED_IMAGE_TYPES.has(file.type.toLowerCase());
}

/**
 * Reconstructs the public url a stored file will be served from, the same way
 * `preparedImageData`/`buildStoredImage` do on their own sides: the raw upload response carries
 * `name` but no `url`, and the CDN base plus the current user's file-manager hash and folder is all
 * that is missing.
 */
export function buildUploadedImageUrl(cdnUrl: string, userHash: string, folder: string, fileName: string): string {
    return `${cdnUrl}/${userHash}${folder}${fileName}`;
}

/** `[image:<url>]` — the literal reference text inserted into the chat message, explained to the model in `ai-chat-drawer.prompt-core.ts`. */
export function formatUploadedImageReference(url: string): string {
    return `[image:${url}]`;
}

const IMAGE_REFERENCE_PATTERN = /\[image:(\S+?)\]/g;

/**
 * Every `[image:<url>]` reference still present in a message, deduped.
 *
 * An UPLOADED screenshot no longer lives in the text while composing — it is held as an attachment
 * on the component and only appended to the outgoing text on send, so the user sees a thumbnail
 * instead of a CDN url. This still runs at send time for the other case: a reference the user typed
 * or pasted by hand, which has no attachment entry behind it but is a reference all the same.
 */
export function extractImageReferenceUrls(text: string): string[] {
    const urls = new Set<string>();

    for (const match of text.matchAll(IMAGE_REFERENCE_PATTERN)) {
        urls.add(match[1]);
    }

    return [...urls];
}

/** Mirrors `EXTENSION_BY_TYPE` in `apps/mcp-backend/src/images/image-import.ts` — same 5 formats the upload accepts, so every url this app produces resolves correctly. */
const MEDIA_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    avif: 'image/avif',
};

/** Infers a media type from the url's file extension — falls back to PNG for anything unrecognized rather than refusing to attach it. */
export function guessImageMediaType(url: string): string {
    const extension = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase() ?? '';

    return MEDIA_TYPE_BY_EXTENSION[extension] ?? 'image/png';
}

/** Picks the first image item out of a paste event's clipboard, if any — `null` when nothing pasted is an image. */
export function imageFileFromClipboard(clipboardData: DataTransfer | null): File | null {
    if (!clipboardData) {
        return null;
    }

    for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];

        if (item.kind === 'file' && item.type.startsWith('image/')) {
            return item.getAsFile();
        }
    }

    return null;
}
